import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';

const countWords = (html) => {
  const text = String(html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
};

const RichTextEditor = ({ value = '', onChange, minHeight = 120 }) => {
  const editorRef = useRef(null);
  const [wordCount, setWordCount] = useState(() => countWords(value));

  // Sync value into innerHTML only when changed externally to avoid cursor jump
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
      setWordCount(countWords(value));
    }
  }, [value]);

  const handleInput = useCallback((e) => {
    const html = e.currentTarget.innerHTML;
    setWordCount(countWords(html));
    if (onChange) {
      onChange(html);
    }
  }, [onChange]);

  const executeCmd = (cmd, val = null) => {
    if (editorRef.current) {
      editorRef.current.focus();
      if (cmd === 'link') {
        const url = prompt('Link to (e.g. /c/panchakarma-retreats/kerala)', '/c/panchakarma-retreats/kerala');
        if (url) {
          document.execCommand('createLink', false, url);
        }
      } else if (cmd === 'formatBlock') {
        document.execCommand('formatBlock', false, val);
      } else {
        document.execCommand(cmd, false, null);
      }
      const html = editorRef.current.innerHTML;
      setWordCount(countWords(html));
      if (onChange) {
        onChange(html);
      }
    }
  };

  const toolbarButtons = [
    { label: 'H2', cmd: 'formatBlock', val: 'h2' },
    { label: 'H3', cmd: 'formatBlock', val: 'h3' },
    { label: 'B', cmd: 'bold' },
    { label: 'I', cmd: 'italic', isItalic: true },
    { label: '• List', cmd: 'insertUnorderedList' },
    { label: '1. List', cmd: 'insertOrderedList' },
    { label: 'Link', cmd: 'link' }
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          border: '1px solid #E2E8F0',
          borderBottom: 'none',
          borderRadius: '6px 6px 0 0',
          p: '6px 8px',
          backgroundColor: '#F8FAFC',
          alignItems: 'center'
        }}
      >
        {toolbarButtons.map((btn) => (
          <Box
            key={btn.label}
            component="button"
            type="button"
            onClick={() => executeCmd(btn.cmd, btn.val)}
            sx={{
              px: '7px',
              py: '2px',
              borderRadius: '4px',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              fontWeight: 500,
              cursor: 'pointer',
              userSelect: 'none',
              fontSize: 11.5,
              color: '#334155',
              fontFamily: 'inherit',
              '&:hover': { borderColor: '#FFBD65', color: '#1E293B' }
            }}
          >
            {btn.isItalic ? <i>I</i> : btn.label}
          </Box>
        ))}
        <Box sx={{ ml: 'auto', color: '#2E7D32', fontWeight: 500, fontSize: 11 }}>
          {wordCount} words
        </Box>
      </Box>
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="hubs-rt"
        onInput={handleInput}
        sx={{
          border: '1px solid #E2E8F0',
          borderRadius: '0 0 6px 6px',
          p: 1.5,
          minHeight,
          lineHeight: 1.55,
          outline: 'none',
          fontSize: 13,
          backgroundColor: '#FFFFFF',
          overflowY: 'auto'
        }}
      />
    </Box>
  );
};

export default React.memo(RichTextEditor);
